// Per-surface starter questions for the AI helper. Solves the "blank input
// anxiety" of users who open the bubble but don't know what to ask.
//
// Curation rules:
//   - Each prompt is something the LLM can actually answer given the
//     surface's context payload (no questions that need data we don't send).
//   - Lead with the user's most likely doubt on this surface, not generic
//     small-talk.
//   - Keep prompts short — they render as clickable chips, not paragraphs.

export type AiSurface = 'quiz' | 'quiz_result' | 'recommendations' | 'map' | 'details' | 'next_steps';

export interface SuggestedPrompt {
  /** Stable id for analytics / React key. */
  id: string;
  /** Visible label (en/ru). */
  label_en: string;
  label_ru: string;
}

const PROMPTS: Record<AiSurface, SuggestedPrompt[]> = {
  quiz: [
    { id: 'why-climate', label_en: 'Why does climate matter so much?', label_ru: 'Почему климат важнее всего?' },
    { id: 'what-budget', label_en: 'What budget tier fits a single remote worker?', label_ru: 'Какой бюджет подходит одинокому удалёнщику?' },
    { id: 'passport-impact', label_en: 'How does my passport affect the result?', label_ru: 'Как мой паспорт влияет на результат?' },
  ],
  quiz_result: [
    { id: 'compare-top2', label_en: 'Why is #1 above #2 for me?', label_ru: 'Почему #1 выше #2 именно для меня?' },
    { id: 'low-dim', label_en: 'What is the weakest dimension in my top match?', label_ru: 'Какая самая слабая ось у моего лидера?' },
    { id: 'cheaper-option', label_en: 'Is there a cheaper option close in fit?', label_ru: 'Есть ли более дешёвый вариант близкий по подходящести?' },
    { id: 'family-priorities', label_en: 'What changes if I add kids?', label_ru: 'Что изменится, если у меня появятся дети?' },
  ],
  recommendations: [
    { id: 'top-region-why', label_en: 'Why does the top region win for my profile?', label_ru: 'Почему лидирующий регион выигрывает для меня?' },
    { id: 'alpine-vs-coast', label_en: 'Alpine vs coastal — which fits me better?', label_ru: 'Альпы или побережье — что мне больше подходит?' },
    { id: 'small-town', label_en: 'Which region has the best small towns?', label_ru: 'В каком регионе лучшие маленькие города?' },
  ],
  map: [
    { id: 'layer-meaning', label_en: 'What does the current layer show?', label_ru: 'Что показывает текущий слой карты?' },
    { id: 'find-comune', label_en: 'Find a comune under 300m with cool summers', label_ru: 'Найди коммуну ниже 300м с прохладным летом' },
    { id: 'no-tourists', label_en: 'Where are the towns without summer tourists?', label_ru: 'Где городки без летних туристов?' },
  ],
  details: [
    { id: 'is-good-for-me', label_en: 'Is this place a good fit for my quiz answers?', label_ru: 'Подходит ли мне это место по моим ответам в квизе?' },
    { id: 'cost-context', label_en: 'How does this cost compare to nearby comuni?', label_ru: 'Как стоимость здесь сравнивается с соседними коммунами?' },
    { id: 'climate-pros-cons', label_en: 'What are the climate downsides here?', label_ru: 'В чём минусы климата здесь?' },
    { id: 'who-thrives', label_en: 'Who thrives here — and who does not?', label_ru: 'Кому здесь хорошо, а кому нет?' },
  ],
  next_steps: [
    { id: 'visa-fastest', label_en: 'What is the fastest visa path here?', label_ru: 'Какой здесь самый быстрый путь визы?' },
    { id: 'tax-residency', label_en: 'When do I become a tax resident?', label_ru: 'Когда я становлюсь налоговым резидентом?' },
    { id: 'order-of-ops', label_en: 'In what order should I do these steps?', label_ru: 'В каком порядке проходить эти шаги?' },
  ],
};

export function getSuggestedPrompts(surface: AiSurface): SuggestedPrompt[] {
  return PROMPTS[surface] ?? [];
}
